import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';

// ================ Async thunks ================ //

/////////////////////////////
// Create Payment Token //
/////////////////////////////
const createPaymentTokenPayloadCreator = (params, { rejectWithValue }) => {
  const { cardData } = params;

  // Initialize Xendit on client side
  if (typeof window === 'undefined' || !window.Xendit) {
    return rejectWithValue(storableError(new Error('Xendit must be loaded')));
  }

  return new Promise((resolve, reject) => {
    window.Xendit.card.createToken(cardData, (err, token) => {
      if (err) {
        log.error(err, 'xendit-create-token-failed', {
          xenditMessage: err.message,
        });
        reject(storableError(err));
      } else {
        resolve(token);
      }
    });
  }).catch(err => {
    return rejectWithValue(storableError(err));
  });
};

export const createPaymentTokenThunk = createAsyncThunk(
  'xendit/createPaymentToken',
  createPaymentTokenPayloadCreator
);

// Backward compatible wrapper function
export const createPaymentToken = params => dispatch => {
  return dispatch(createPaymentTokenThunk(params)).unwrap();
};

/////////////////////////////
// Confirm Card Payment //
/////////////////////////////
const confirmCardPaymentPayloadCreator = (params, { rejectWithValue }) => {
  const { tokenId, amount, orderId } = params;

  // Call backend API to charge the card
  return fetch('/api/xendit-charge', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tokenId,
      amount,
      orderId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(data => {
      return { ...data, transactionId: orderId };
    })
    .catch(err => {
      const e = storableError(err);
      log.error(e, 'xendit-confirm-card-payment-failed', {
        xenditMessage: e.message,
      });
      return rejectWithValue(e);
    });
};

export const confirmCardPaymentThunk = createAsyncThunk(
  'xendit/confirmCardPayment',
  confirmCardPaymentPayloadCreator
);

// Backward compatible wrapper function
export const confirmCardPayment = params => dispatch => {
  return dispatch(confirmCardPaymentThunk(params)).unwrap();
};

///////////////////////
// Handle Card Setup //
///////////////////////
const handleCardSetupPayloadCreator = (params, { rejectWithValue }) => {
  const { cardData } = params;

  if (typeof window === 'undefined' || !window.Xendit) {
    return rejectWithValue(storableError(new Error('Xendit must be loaded')));
  }

  return new Promise((resolve, reject) => {
    window.Xendit.card.createToken(cardData, (err, token) => {
      if (err) {
        log.error(err, 'xendit-handle-card-setup-failed', {
          xenditMessage: err.message,
        });
        reject(storableError(err));
      } else {
        // Store token for later use
        resolve({ setupIntent: { payment_method: token.id, status: 'succeeded' } });
      }
    });
  }).catch(err => {
    return rejectWithValue(storableError(err));
  });
};

export const handleCardSetupThunk = createAsyncThunk(
  'xendit/handleCardSetup',
  handleCardSetupPayloadCreator
);

// Backward compatible wrapper function
export const handleCardSetup = params => dispatch => {
  return dispatch(handleCardSetupThunk(params)).unwrap();
};

// ================ Slice ================ //

const xenditSlice = createSlice({
  name: 'xendit',
  initialState: {
    confirmCardPaymentInProgress: false,
    confirmCardPaymentError: null,
    handleCardSetupInProgress: false,
    handleCardSetupError: null,
    paymentIntent: null,
    setupIntent: null,
    retrievePaymentIntentInProgress: false,
    retrievePaymentIntentError: null,
  },
  reducers: {
    xenditAccountClearError: state => {
      return {
        confirmCardPaymentInProgress: false,
        confirmCardPaymentError: null,
        handleCardSetupInProgress: false,
        handleCardSetupError: null,
        paymentIntent: null,
        setupIntent: null,
        retrievePaymentIntentInProgress: false,
        retrievePaymentIntentError: null,
      };
    },
    initializeCardPaymentData: state => {
      state.confirmCardPaymentInProgress = false;
      state.confirmCardPaymentError = null;
      state.paymentIntent = null;
    },
  },
  extraReducers: builder => {
    builder
      // Confirm Card Payment cases
      .addCase(confirmCardPaymentThunk.pending, state => {
        state.confirmCardPaymentError = null;
        state.confirmCardPaymentInProgress = true;
      })
      .addCase(confirmCardPaymentThunk.fulfilled, (state, action) => {
        state.paymentIntent = action.payload;
        state.confirmCardPaymentInProgress = false;
      })
      .addCase(confirmCardPaymentThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.confirmCardPaymentError = action.payload;
        state.confirmCardPaymentInProgress = false;
      })
      // Handle Card Setup cases
      .addCase(handleCardSetupThunk.pending, state => {
        state.handleCardSetupError = null;
        state.handleCardSetupInProgress = true;
      })
      .addCase(handleCardSetupThunk.fulfilled, (state, action) => {
        state.setupIntent = action.payload;
        state.handleCardSetupInProgress = false;
      })
      .addCase(handleCardSetupThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.handleCardSetupError = action.payload;
        state.handleCardSetupInProgress = false;
      });
  },
});

// Export the action creators
export const { xenditAccountClearError, initializeCardPaymentData } = xenditSlice.actions;

export default xenditSlice.reducer;
